import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";

const NAV_LINKS = [
  { label: "New Drop", to: "/#drop" },
  { label: "Tracksuits", to: "/tracksuits" },
  { label: "Accessories", to: "/accessories" },
];

const SearchBar = ({ onClose, onSubmit }) => {
  const [query, setQuery] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    onSubmit(query.trim());
    setQuery("");
  };

  return (
    <div className="border-t border-white/10 bg-capra-night/95 backdrop-blur-xl">
      <form onSubmit={submit} className="mx-auto max-w-3xl px-6 py-5 flex items-center gap-4">
        <Search size={16} className="text-capra-bronze shrink-0" strokeWidth={1.5} />
        <input
          data-testid="search-input"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the collection…"
          className="flex-1 bg-transparent outline-none text-capra-ivory placeholder:text-capra-muted/60 text-sm tracking-wide"
        />
        <button type="button" onClick={onClose} className="text-capra-muted hover:text-capra-ivory transition-colors" aria-label="Close search">
          <X size={16} strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
};

const MobileMenu = ({ open, setOpen, user }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="left" aria-describedby={undefined} className="w-[85vw] max-w-sm bg-capra-night border-r border-capra-bronze/20 p-0" data-testid="mobile-menu">
      <SheetTitle className="sr-only">Menu</SheetTitle>
      <div className="leather-panel h-full flex flex-col">
        <div className="px-8 pt-10 pb-6 flex items-center gap-3">
          <img src="/brand/goat-badge.jpg" alt="" className="h-10 w-10 rounded-full object-cover border border-capra-bronze/50" />
          <span className="font-display text-base tracking-[0.28em] text-capra-ivory">CAPRA FALCONERI</span>
        </div>
        <div className="hairline mx-8" />
        <nav className="flex flex-col px-8 py-8 gap-5">
          {NAV_LINKS.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.to.slice(1)}`}
              onClick={() => setOpen(false)}
              style={{ animationDelay: `${i * 60}ms` }}
              className="animate-fade-up font-display text-2xl text-capra-ivory/90 hover:text-capra-bronze transition-colors"
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hairline mx-8" />
        <div className="px-8 py-6 flex flex-col gap-4 text-[11px] uppercase tracking-[0.22em] text-capra-muted">
          <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} className="hover:text-capra-bronze transition-colors">
            {user ? "My Account" : "Sign In"}
          </Link>
          <Link to="/wishlist" onClick={() => setOpen(false)} className="hover:text-capra-bronze transition-colors">
            Wishlist
          </Link>
          <Link to="/contact" onClick={() => setOpen(false)} className="hover:text-capra-bronze transition-colors">
            Contact
          </Link>
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

const HeaderIcons = ({ user, wishlistCount, cartCount, onSearchToggle, onCartOpen }) => (
  <div className="flex items-center gap-1 sm:gap-2">
    <button data-testid="search-toggle-button" onClick={onSearchToggle} className="p-2 text-capra-ivory/80 hover:text-capra-bronze transition-colors" aria-label="Search">
      <Search size={18} strokeWidth={1.5} />
    </button>
    <Link to={user ? "/account" : "/login"} data-testid="account-link" className="p-2 text-capra-ivory/80 hover:text-capra-bronze transition-colors" aria-label="Account">
      <User size={18} strokeWidth={1.5} />
    </Link>
    <Link to="/wishlist" data-testid="wishlist-link" className="relative p-2 text-capra-ivory/80 hover:text-capra-bronze transition-colors" aria-label="Wishlist">
      <Heart size={18} strokeWidth={1.5} />
      {wishlistCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-capra-bronze text-capra-night text-[9px] font-semibold flex items-center justify-center">
          {wishlistCount}
        </span>
      )}
    </Link>
    <button data-testid="cart-button" onClick={onCartOpen} className="relative p-2 text-capra-ivory/80 hover:text-capra-bronze transition-colors" aria-label="Cart">
      <ShoppingBag size={18} strokeWidth={1.5} />
      {cartCount > 0 && (
        <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-capra-bronze text-capra-night text-[9px] font-semibold flex items-center justify-center">
          {cartCount}
        </span>
      )}
    </button>
  </div>
);

const BrandLogo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center group">
    <span className="font-display text-sm sm:text-base lg:text-lg tracking-[0.32em] text-capra-ivory whitespace-nowrap group-hover:text-capra-bronze transition-colors">
      CAPRA FALCONERI
    </span>
  </Link>
);

const DesktopNav = () => (
  <nav className="hidden lg:flex items-center gap-7 mx-auto" data-testid="desktop-nav">
    {NAV_LINKS.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={`nav-link-${l.to.slice(1)}`}
        className={({ isActive }) =>
          `gold-underline text-[11px] uppercase tracking-[0.22em] font-medium transition-colors ${
            isActive ? "text-capra-bronze" : "text-capra-ivory/80 hover:text-capra-ivory"
          }`
        }
      >
        {l.label}
      </NavLink>
    ))}
  </nav>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { count, setDrawerOpen } = useCart();
  const { ids } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (q) => {
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 border-b ${
          scrolled ? "bg-capra-night/85 backdrop-blur-xl border-white/10" : "bg-gradient-to-b from-capra-night/80 to-transparent border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10">
          <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden p-2 -ml-2 text-capra-ivory hover:text-capra-bronze transition-colors"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>

            <BrandLogo />

            <DesktopNav />

            <HeaderIcons
              user={user}
              wishlistCount={ids.length}
              cartCount={count}
              onSearchToggle={() => setSearchOpen((v) => !v)}
              onCartOpen={() => setDrawerOpen(true)}
            />
          </div>
        </div>

        {searchOpen && <SearchBar onClose={() => setSearchOpen(false)} onSubmit={handleSearch} />}
      </header>

      <MobileMenu open={menuOpen} setOpen={setMenuOpen} user={user} />
    </>
  );
};
